import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { MaskedLine, Eyebrow } from "@/components/Reveal";
import { BOOKS } from "@/data/books";

export default function Hero() {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [0, 1], [8, -8]);
  const rotateY = useTransform(sx, [0, 1], [-10, 10]);
  const glowX = useTransform(sx, [0, 1], ["30%", "70%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  const stack = [BOOKS[3], BOOKS[0], BOOKS[2]];

  return (
    <section
      data-testid="hero-section"
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
    >
      <motion.div
        className="absolute w-[46rem] h-[46rem] rounded-full bg-terracotta/15 blur-[120px] -top-40 -right-40 pointer-events-none"
        style={{ left: glowX }}
      />
      <div className="absolute w-[30rem] h-[30rem] rounded-full bg-sage/10 blur-[100px] bottom-0 -left-32 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative z-10 w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-7 inline-flex items-center gap-3 border border-sand bg-white/60 rounded-full pl-2 pr-5 py-1.5"
          >
            <span className="w-8 h-8 rounded-full bg-sage text-cream font-display font-bold text-xs flex items-center justify-center">DA</span>
            <Eyebrow className="!tracking-[0.18em]">The Bailey series · by Damali Allison</Eyebrow>
          </motion.div>

          <h1 data-testid="hero-title" className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-walnut">
            <MaskedLine i={0}>Every child deserves</MaskedLine>
            <MaskedLine i={1}>
              to feel <em className="not-italic text-terracotta">proud.</em>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-walnutsoft"
          >
            Confidence-driven picture books that help young minds dream big, believe in
            themselves, and celebrate exactly who they are — from first days at school to
            first trips to the dentist.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="/books"
              data-testid="hero-explore-books-button"
              onClick={(e) => { e.preventDefault(); window.__lenis ? (window.location.href = "/books") : (window.location.href = "/books"); }}
              className="group rounded-full bg-terracotta text-cream pl-7 pr-6 py-4 font-display font-bold text-sm sm:text-base flex items-center gap-2 hover:bg-terracottadark transition-colors duration-300 shadow-[0_18px_36px_-14px_rgba(224,109,83,0.6)]"
            >
              Explore the books
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#schools"
              data-testid="hero-school-visit-button"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("schools");
                if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -70 }) : el.scrollIntoView({ behavior: "smooth" });
              }}
              className="rounded-full border-2 border-walnut text-walnut px-7 py-[0.85rem] font-display font-bold text-sm sm:text-base hover:bg-walnut hover:text-cream transition-colors duration-300"
            >
              Book a school visit
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-trust-badge"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex items-center gap-3"
          >
            <span className="flex text-amberbrand">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            <p className="text-sm text-walnutsoft">
              Rated <strong className="text-walnut">5.0</strong> by families on Amazon UK · Loved in classrooms across the UK
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative h-[26rem] sm:h-[30rem] [perspective:1200px]">
          <motion.div
            data-testid="hero-book-stack"
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full h-full"
          >
            <motion.img
              src={stack[0].cover}
              alt={stack[0].title}
              className="absolute left-0 top-16 w-44 sm:w-52 rounded-xl shadow-[0_30px_50px_-20px_rgba(44,34,30,0.45)] animate-float-slower"
              style={{ transform: "translateZ(-60px) rotate(6deg)" }}
            />
            <motion.img
              src={stack[1].cover}
              alt={stack[1].title}
              className="absolute left-1/2 -translate-x-1/2 top-0 w-56 sm:w-72 rounded-xl shadow-[0_40px_70px_-24px_rgba(44,34,30,0.5)] animate-float-slow z-10"
              style={{ transform: "translateZ(60px)" }}
            />
            <motion.img
              src={stack[2].cover}
              alt={stack[2].title}
              className="absolute right-0 bottom-2 w-40 sm:w-48 rounded-xl shadow-[0_30px_50px_-20px_rgba(44,34,30,0.45)] animate-float-slower"
              style={{ transform: "translateZ(-30px) rotate(-7deg)" }}
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-walnutmuted"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          className="w-px h-8 bg-walnutmuted/60"
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}
